"use client"

import {
  authIdentity,
  prepareBroadcastRequest,
  utils,
} from "@defuse-protocol/internal-utils"
import { authenticateAndStoreTokens } from "@src/actions/auth"
import { config } from "@src/components/DefuseSDK/config"
import { nearClient } from "@src/components/DefuseSDK/constants/nearClient"
import { createVerificationMessage } from "@src/components/DefuseSDK/core/messages"
import { hasPublicKey } from "@src/components/DefuseSDK/services/intentsContractService"
import type { Transaction } from "@src/components/DefuseSDK/types/deposit"
import { extractWalletErrorCode } from "@src/components/DefuseSDK/utils/walletErrorExtractor"
import { WalletBannedDialog } from "@src/components/WalletBannedDialog"
import { WalletVerificationDialog } from "@src/components/WalletVerificationDialog"
import { ChainType, useConnectWallet } from "@src/hooks/useConnectWallet"
import { useWalletAgnosticSignMessage } from "@src/hooks/useWalletAgnosticSignMessage"
import {
  type VerificationResult,
  walletVerificationMachine,
} from "@src/machines/walletVerificationMachine"
import {
  useBootstrapActor,
  useBootstrapSelector,
} from "@src/providers/AppBootstrapProvider"
import { useNearWallet } from "@src/providers/NearWalletProvider"
import { useBypassedWalletsStore } from "@src/stores/useBypassedWalletsStore"
import { hashForAnalytics } from "@src/utils/analyticsSanitize"
import { isAnalyticsSuppressed } from "@src/utils/analyticsSuppression"
import { WalletConnectionFailure } from "@src/utils/classifyWalletConnectionFailure"
import { logger } from "@src/utils/logger"
import { trackEvent } from "@src/utils/trackEvent"
import { useQuery } from "@tanstack/react-query"
import { useActor } from "@xstate/react"
import type { AccessKeyList } from "near-api-js/lib/providers/provider"
import { useCallback, useEffect, useRef, useState } from "react"
import { fromPromise } from "xstate"
import { useMixpanel } from "./MixpanelProvider"

export function WalletVerificationProvider() {
  const { state, signOut } = useConnectWallet()
  const mixPanel = useMixpanel()
  const bootstrapActor = useBootstrapActor()
  const machineState = useBootstrapSelector((snap) => snap.value as string)
  const unsafeWallet = useBootstrapSelector((snap) => snap.context.unsafeWallet)

  const bannedAccountCheck = useQuery({
    queryKey: ["banned_account", state.address, state.chainType],
    queryFn: async () => {
      if (!state.address || !state.chainType) {
        return { isBanned: false }
      }
      const response = await fetch(
        `/api/account/validate-banned?address=${encodeURIComponent(state.address)}&chainType=${encodeURIComponent(state.chainType)}`
      )
      if (!response.ok) {
        throw new Error("Failed to validate banned account")
      }
      return response.json() as Promise<{
        isBanned: boolean
        accountId: string | null
      }>
    },
    enabled:
      machineState === "awaiting_safety" &&
      state.address != null &&
      state.chainType != null,
  })

  const needsSafetyCheck = state.chainType === "evm"

  const safetyCheck = useQuery({
    queryKey: ["address_safety", state.address],
    queryFn: async () => {
      if (needsSafetyCheck) {
        const response = await fetch(`/api/addresses/${state.address}/safety`)
        if (!response.ok) {
          throw new Error("Failed to check safety status")
        }
        return response.json() as Promise<{ safetyStatus: "safe" | "unsafe" }>
      }
      // For non-EVM wallets, skip the safety API check
      return { safetyStatus: "safe" as const }
    },
    enabled:
      machineState === "awaiting_safety" &&
      state.address != null &&
      state.chainType != null,
    staleTime: 1000 * 60 * 60, // 1 hour
  })

  const { addBypassedWalletAddress, isWalletBypassed } =
    useBypassedWalletsStore()

  // Feed safety/banned results to the bootstrap machine.
  // Wait for the banned check to settle before forwarding safety results,
  // so we never accidentally send SAFETY_CLEAR while banned status is unknown.
  useEffect(() => {
    if (machineState !== "awaiting_safety") return

    // Banned check takes priority — wait until it resolves
    if (bannedAccountCheck.isLoading) return

    if (bannedAccountCheck.data?.isBanned) {
      bootstrapActor.send({ type: "BANNED_DETECTED" })
      return
    }

    if (safetyCheck.data) {
      if (safetyCheck.data.safetyStatus === "safe") {
        bootstrapActor.send({ type: "SAFETY_CLEAR" })
      } else {
        bootstrapActor.send({ type: "SAFETY_UNSAFE" })
      }
    }
  }, [
    machineState,
    bannedAccountCheck.data,
    bannedAccountCheck.isLoading,
    safetyCheck.data,
    bootstrapActor,
  ])

  // In awaiting_safety with unsafeWallet: show WalletBannedDialog (unless bypassed)
  if (
    machineState === "awaiting_safety" &&
    unsafeWallet &&
    state.address != null &&
    !isWalletBypassed(state.address)
  ) {
    return (
      <WalletBannedDialog
        open={true}
        onAbort={() => {
          bootstrapActor.send({ type: "SIGN_OUT" })
          if (state.chainType != null) {
            void signOut({ id: state.chainType })
          }
        }}
        onBypass={() => {
          if (state.address != null) {
            addBypassedWalletAddress(state.address)
            bootstrapActor.send({ type: "SAFETY_CLEAR" })
            if (isAnalyticsSuppressed()) return
            void hashForAnalytics(state.address)
              .then((hashedAddress) => {
                const payload = {
                  wallet_hash: hashedAddress,
                  wallet_type: state.chainType,
                }
                mixPanel?.track("wallet_bypassed", payload)
                window.gtag?.("event", "wallet_bypassed", payload)
              })
              .catch(() => {})
          }
        }}
      />
    )
  }

  // In needs_verification: show the verification UI
  if (machineState === "needs_verification" && state.address != null) {
    const verificationUI = (
      <WalletVerificationUI
        isSessionExpired={state.isSessionExpired}
        onVerified={() => {
          // Machine re-enters checking_session which invokes checkSession directly
          bootstrapActor.send({ type: "VERIFICATION_COMPLETE" })
        }}
        onAbort={() => {
          bootstrapActor.send({ type: "SIGN_OUT" })
          if (state.chainType != null) {
            void signOut({ id: state.chainType })
          }
        }}
      />
    )

    // Named NEAR accounts must register their public key on the intents
    // contract BEFORE auth verification — the contract can't verify NEP-413
    // signatures for keys it doesn't know about.
    if (
      state.chainType === ChainType.Near &&
      !utils.isImplicitAccount(state.address)
    ) {
      return (
        <NearPublicKeyCheck
          accountId={state.address}
          onSignOut={() => {
            bootstrapActor.send({ type: "SIGN_OUT" })
            void signOut({ id: ChainType.Near })
          }}
        >
          {verificationUI}
        </NearPublicKeyCheck>
      )
    }

    return verificationUI
  }

  return null
}

function WalletVerificationUI({
  isSessionExpired,
  onVerified,
  onAbort,
}: {
  isSessionExpired: boolean
  /** Called when verification succeeds; JWT is already set in httpOnly cookie by server */
  onVerified: () => void
  onAbort: () => void
}) {
  const { state: unconfirmedWallet } = useConnectWallet()

  const signMessage = useWalletAgnosticSignMessage()
  const mixPanel = useMixpanel()

  const [state, send, serviceRef] = useActor(
    walletVerificationMachine.provide({
      actions: {
        logError: (_, { error }: { error: unknown }) => {
          const failure = new WalletConnectionFailure(
            error,
            extractWalletErrorCode(error, null)
          )
          trackEvent("wallet_verification_failed", {
            chain: unconfirmedWallet.chainType ?? "unknown",
            failure_type: failure.failureType,
            error_message: failure.truncatedMessage,
          })
          if (failure.isActionable) {
            logger.error("Wallet verification failed", {
              error: failure,
              chain: unconfirmedWallet.chainType ?? "unknown",
            })
          }
        },
      },
      actors: {
        verifyWallet: fromPromise(
          async ({ signal }): Promise<VerificationResult> => {
            if (
              unconfirmedWallet.address == null ||
              unconfirmedWallet.chainType == null
            ) {
              return { success: false }
            }

            const intentsUserId = authIdentity.authHandleToIntentsUserId(
              unconfirmedWallet.address,
              unconfirmedWallet.chainType
            )

            const walletMessage = await createVerificationMessage(
              { signerId: intentsUserId },
              unconfirmedWallet.chainType
            )
            const walletSignature = await signMessage(walletMessage)

            const signedData = prepareBroadcastRequest.prepareSwapSignedData(
              walletSignature,
              {
                userAddress: unconfirmedWallet.address,
                userChainType: unconfirmedWallet.chainType,
              }
            )

            // Guard: don't write cookies if user cancelled during signing
            if (signal.aborted) {
              return { success: false }
            }

            const result = await authenticateAndStoreTokens({
              signedData,
              address: unconfirmedWallet.address,
              authMethod: unconfirmedWallet.chainType,
            })

            if (result.success) {
              return {
                success: true,
                expiresAt: result.expiresAt,
              }
            }

            return { success: false }
          }
        ),
      },
    })
  )

  const onVerifiedRef = useRef(onVerified)
  onVerifiedRef.current = onVerified
  const onAbortRef = useRef(onAbort)
  onAbortRef.current = onAbort

  useEffect(
    () =>
      serviceRef.subscribe((machineState) => {
        if (machineState.matches("verified")) {
          onVerifiedRef.current()
          if (!isAnalyticsSuppressed()) {
            void hashForAnalytics(unconfirmedWallet.address)
              .then((hashedAddress) => {
                const payload = {
                  wallet_hash: hashedAddress,
                  wallet_type: unconfirmedWallet.chainType,
                }
                mixPanel?.track("wallet_verified", payload)
                window.gtag?.("event", "wallet_verified", payload)
              })
              .catch(() => {})
          }
        }
        if (machineState.matches("aborted")) {
          onAbortRef.current()
        }
      }).unsubscribe,
    [
      serviceRef,
      mixPanel,
      unconfirmedWallet.address,
      unconfirmedWallet.chainType,
    ]
  )

  return (
    <WalletVerificationDialog
      open={true}
      onConfirm={() => {
        send({ type: "START" })
      }}
      onCancel={() => {
        send({ type: "ABORT" })
      }}
      isVerifying={state.matches("verifying")}
      isFailure={state.context.hadError}
      isSessionExpired={isSessionExpired}
      isPasskey={unconfirmedWallet.chainType === ChainType.WebAuthn}
    />
  )
}

/**
 * Checks if a NEAR explicit account has its public key registered on the
 * intents contract. If not, prompts the user to send an add_public_key
 * transaction. Rejecting signs the user out.
 */
function NearPublicKeyCheck({
  accountId,
  onSignOut,
  children,
}: {
  accountId: string
  onSignOut: () => void
  /** Rendered when public key is already registered (no registration needed) */
  children?: React.ReactNode
}) {
  const nearWallet = useNearWallet()
  const [isAdding, setIsAdding] = useState(false)

  const pkCheck = useQuery({
    queryKey: ["near_pubkey_check", accountId],
    queryFn: async (): Promise<{
      needsRegistration: boolean
      publicKey: string | null
    }> => {
      // 1. Try to get public key from wallet's getAccounts()
      let publicKey: string | null = null
      if (nearWallet.connector != null) {
        const wallet = await nearWallet.connector.wallet()
        const accounts = await wallet.getAccounts()
        const account = accounts.find((a) => a.accountId === accountId)
        publicKey = account?.publicKey ?? null
      }

      // 2. If wallet provided the key, check just that one
      if (publicKey != null) {
        const registered = await hasPublicKey({
          nearClient,
          accountId,
          publicKey,
        })
        return { needsRegistration: !registered, publicKey }
      }

      // 3. Fallback: query NEAR RPC for all access keys
      const response = await nearClient.query<AccessKeyList>({
        request_type: "view_access_key_list",
        account_id: accountId,
        finality: "optimistic",
      })
      const fullAccessKeys = response.keys.filter(
        (k) => k.access_key.permission === "FullAccess"
      )

      // 4. Check if ANY full-access key is registered on intents contract
      for (const key of fullAccessKeys) {
        const registered = await hasPublicKey({
          nearClient,
          accountId,
          publicKey: key.public_key,
        })
        if (registered) {
          return { needsRegistration: false, publicKey: null }
        }
      }

      // 5. None registered — use first full-access key for registration
      const keyToRegister = fullAccessKeys[0]?.public_key ?? null
      return {
        needsRegistration: keyToRegister != null,
        publicKey: keyToRegister,
      }
    },
    enabled: nearWallet.connector != null,
    retry: 1,
  })

  const publicKeyToRegister = pkCheck.data?.publicKey ?? null

  const handleConfirm = useCallback(async () => {
    if (publicKeyToRegister == null) return
    setIsAdding(true)
    try {
      const tx: Transaction["NEAR"] = {
        receiverId: config.env.contractID,
        actions: [
          {
            type: "FunctionCall",
            params: {
              methodName: "add_public_key",
              args: { public_key: publicKeyToRegister },
              gas: "5000000000000",
              deposit: "1",
            },
          },
        ],
      }
      await nearWallet.signAndSendTransactions({ transactions: [tx] })
      await pkCheck.refetch()
    } catch (err) {
      logger.error(err)
      onSignOut()
    } finally {
      setIsAdding(false)
    }
  }, [publicKeyToRegister, nearWallet, pkCheck, onSignOut])

  // Still loading — show nothing while checking
  if (pkCheck.isLoading) {
    return null
  }

  // No registration needed — render children (next step) or nothing
  if (pkCheck.data?.needsRegistration !== true) {
    return <>{children}</>
  }

  return (
    <WalletVerificationDialog
      open={true}
      onConfirm={handleConfirm}
      onCancel={onSignOut}
      isVerifying={false}
      isFailure={false}
      isConfirmingPubKey={!isAdding}
      isAddingPubKey={isAdding}
      accountId={accountId}
    />
  )
}
